// Dynamic Table — the spreadsheet-style authoring model for data-driven tables
// (PDF-Generator-API-style): row 0 = HEADER (type the column titles), row 1 = the
// TEMPLATE row (type {{field}} / Liquid; repeats per data record at render time),
// rows 2+ = SUMMARY rows (rendered once after the data, Liquid with ROOT scope —
// e.g. {{ rows | sum: "amount" | money }}).
//
// Built on the vendored interactive Fabric Table extension, so authoring gets
// double-click cell editing, cell selection, column drag-resize and the existing
// cell-styling panel for free. The canvas object IS the design; toSpec() converts
// cells + styling into the server report spec (report.ts), which does the actual
// data binding + pagination. The server never renders this object directly — it
// consumes the embedded `spec` and strips the object from the static layer.
import { classRegistry } from 'fabric'
import { Table } from './table'
import { fieldsIn, lq, rowScope } from './../liquid'
import { getCanvas, state } from './../state'
import { labelPx } from './../canvas'
import { enableTableInteraction } from './table-widget'

const PT_PER_PX = 72 / 96

export interface DynamicTableOptions {
  binding?: { path?: string }
  repeatHeader?: boolean
  altRowFill?: string
  rowHeightPt?: number
  bandFont?: string
}

const HEADER_FILL = '#111827'
const HEADER_TEXT = '#ffffff'

type CellStyles = {
  fontStyle?: 'bold' | 'italic' | 'bolditalic'
  textColor?: string
  fillColor?: string
  halign?: 'left' | 'center' | 'right'
  fontSize?: number
}

function colorOrUndef(v: unknown): string | undefined {
  return typeof v === 'string' && v.startsWith('#') ? v : undefined
}

export class DynamicTable extends Table {
  static type = 'DynamicTable'

  declare binding: { path?: string }
  declare repeatHeader: boolean
  declare altRowFill?: string
  declare rowHeightPt?: number
  declare bandFont?: string

  constructor(rows = 3, cols = 3, options: Record<string, any> = {}) {
    super(Math.max(2, rows), cols, options)
    this.binding = options.binding ?? { path: 'rows' }
    this.repeatHeader = options.repeatHeader ?? true
    this.altRowFill = options.altRowFill
    this.rowHeightPt = options.rowHeightPt
    this.bandFont = options.bandFont
    this.lockRotation = true
  }

  /** Per-cell PDF styles derived from the canvas cell (used for summary rows). */
  private cellStyles(r: number, c: number): CellStyles {
    const cell: any = this.getCell(r, c)
    const tx: any = this.getCellText(r, c)
    const bold = tx?.fontWeight === 'bold'
    const italic = tx?.fontStyle === 'italic'
    const s: CellStyles = {}
    if (bold && italic) s.fontStyle = 'bolditalic'
    else if (bold) s.fontStyle = 'bold'
    else if (italic) s.fontStyle = 'italic'
    const txColor = colorOrUndef(tx?.fill)
    if (txColor) s.textColor = txColor
    const fill = cell?._hasCustomFill ? colorOrUndef(cell?.fill) : undefined
    if (fill) s.fillColor = fill
    if (tx?.textAlign && tx.textAlign !== 'left') s.halign = tx.textAlign
    if (tx?.fontSize) s.fontSize = Math.round(tx.fontSize * PT_PER_PX * 10) / 10
    return s
  }

  /** The server-consumable report spec (DataTableSpec shape, pt geometry). */
  toSpec(): Record<string, any> {
    this.setCoords()
    const sX = this.scaleX ?? 1
    const tl = this.calcACoords().tl // top-left in canvas px regardless of origin
    const widthsPt = this.columnWidths.map((w) => Math.round(w * sX * PT_PER_PX * 10) / 10)

    const headTx: any = this.getCellText(0, 0)
    const tmplTx0: any = this.getCellText(1, 0)

    const columns = Array.from({ length: this.cols }, (_, c) => {
      const tmpl = this.getCellText(1, c)?.text ?? ''
      const align = (this.getCellText(1, c) as any)?.textAlign as string | undefined
      return {
        header: this.getCellText(0, c)?.text ?? '',
        field: fieldsIn(tmpl)[0] ?? `col_${c + 1}`,
        cell: tmpl,
        align: align === 'center' || align === 'right' ? align : 'left',
        width: widthsPt[c],
      }
    })

    const summaryRows = Array.from({ length: Math.max(0, this.rows - 2) }, (_, i) =>
      Array.from({ length: this.cols }, (_, c) => ({
        content: this.getCellText(i + 2, c)?.text ?? '',
        styles: this.cellStyles(i + 2, c),
      })))

    const hasBorders = (this.cellStrokeWidth ?? 0) > 0 && this.cellStroke !== 'transparent'
    const head: Record<string, any> = {
      fillColor: (this.getCell(0, 0) as any)?._hasCustomFill ? colorOrUndef((this.getCell(0, 0) as any)?.fill) ?? HEADER_FILL : HEADER_FILL,
      textColor: colorOrUndef(headTx?.fill) ?? 255,
      fontStyle: headTx?.fontWeight === 'bold' ? 'bold' : 'normal',
    }
    if (headTx?.fontSize) head.fontSize = Math.round(headTx.fontSize * PT_PER_PX * 10) / 10
    const body: Record<string, any> = {}
    const bodyColor = colorOrUndef(tmplTx0?.fill)
    if (bodyColor) body.textColor = bodyColor
    if (tmplTx0?.fontSize) body.fontSize = Math.round(tmplTx0.fontSize * PT_PER_PX * 10) / 10

    return {
      type: 'DataTable', // shape-compatible with the server's DataTableSpec
      left: Math.round(tl.x * PT_PER_PX),
      top: Math.round(tl.y * PT_PER_PX),
      width: Math.round(widthsPt.reduce((a, b) => a + b, 0)),
      binding: this.binding,
      columns,
      summaryRows: summaryRows.length ? summaryRows : undefined,
      repeatHeader: this.repeatHeader,
      rowHeight: this.rowHeightPt,
      font: this.bandFont ?? (tmplTx0?.fontFamily && tmplTx0.fontFamily !== 'Times New Roman' ? tmplTx0.fontFamily : undefined),
      style: {
        borders: hasBorders ? 'grid' : 'none',
        head,
        body,
        altRowFill: this.altRowFill,
      },
    }
  }

  // ---- Ghost preview row -----------------------------------------------------
  // While the table is SELECTED and a dataset is imported, draw one evaluated row
  // (the first data record through the template-row Liquid) just below the table,
  // plus a "repeats ×N" hint. Visual only: not part of layout or serialization.
  // Drawing only-when-selected also keeps it out of PNG/PDF exports, which call
  // discardActiveObject() before snapshotting the live canvas.
  private _pvKey = ''
  private _pvCells: string[] = []

  private previewCells(): string[] | null {
    const first = state.batchData?.[0]
    if (!first || this.rows < 2) return null
    const tpls = Array.from({ length: this.cols }, (_, c) => this.getCellText(1, c)?.text ?? '')
    const key = `${tpls.join('')}|${state.batchData.length}`
    if (key !== this._pvKey) {
      this._pvKey = key
      this._pvCells = tpls.map((t) => lq(t, rowScope(first)))
    }
    return this._pvCells
  }

  override render(ctx: CanvasRenderingContext2D): void {
    super.render(ctx)
    if (this.canvas?.getActiveObject?.() !== this) return
    const cells = this.previewCells()
    if (!cells) return

    const m = this.calcTransformMatrix()
    ctx.save()
    ctx.transform(m[0], m[1], m[2], m[3], m[4], m[5])

    const w = this.width, x0 = -w / 2, y = this.height / 2
    const rowH = 24
    const pad = this.cellPadding ?? 6

    ctx.globalAlpha = 0.92
    ctx.fillStyle = '#eff6ff'
    ctx.fillRect(x0, y, w, rowH)
    ctx.strokeStyle = '#93c5fd'
    ctx.lineWidth = 1
    ctx.setLineDash([4, 3])
    ctx.strokeRect(x0, y, w, rowH)
    ctx.setLineDash([])

    ctx.font = 'italic 11px Inter, system-ui, sans-serif'
    ctx.fillStyle = '#1d4ed8'
    ctx.textBaseline = 'middle'
    let cx = x0
    const widths = this.columnWidths
    cells.forEach((text, c) => {
      const cw = widths[c] ?? 0
      const align = (this.getCellText(1, c) as any)?.textAlign ?? 'left'
      ctx.save()
      ctx.beginPath(); ctx.rect(cx, y, cw, rowH); ctx.clip()
      ctx.textAlign = align === 'right' ? 'right' : align === 'center' ? 'center' : 'left'
      const tx = align === 'right' ? cx + cw - pad : align === 'center' ? cx + cw / 2 : cx + pad
      ctx.fillText(text, tx, y + rowH / 2)
      ctx.restore()
      cx += cw
    })

    ctx.font = '10px Inter, system-ui, sans-serif'
    ctx.fillStyle = '#64748b'
    ctx.textAlign = 'left'
    ctx.fillText(`↻ preview · this row repeats for each of ${state.batchData.length} imported rows`, x0, y + rowH + 11)
    ctx.restore()
  }

  override toObject(propertiesToInclude: any[] = []): any {
    return {
      ...super.toObject(propertiesToInclude),
      type: 'DynamicTable',
      binding: this.binding,
      repeatHeader: this.repeatHeader,
      altRowFill: this.altRowFill,
      rowHeightPt: this.rowHeightPt,
      bandFont: this.bandFont,
      spec: this.toSpec(), // what the server consumes (report.ts)
    }
  }

  // Mirrors Table.fromObject (static, hard-codes `new Table`) but constructs a
  // DynamicTable and restores the dynamic props. Keep in sync if the vendor updates.
  static override async fromObject(object: any): Promise<DynamicTable> {
    const table = new DynamicTable(object.rows ?? 3, object.cols ?? 3, {
      cellWidth: object.columnWidths?.[0] ?? 100,
      minCellHeight: object.minCellHeight,
      cellPadding: object.cellPadding,
      cellSpacing: object.cellSpacing ?? 0,
      cellFill: object.cellFill,
      cellStroke: object.cellStroke,
      cellStrokeWidth: object.cellStrokeWidth ?? 1,
      reflowOriginX: object.reflowOriginX,
      reflowOriginY: object.reflowOriginY,
      originX: object.originX,
      originY: object.originY,
      left: object.left,
      top: object.top,
      scaleX: object.scaleX,
      scaleY: object.scaleY,
      angle: object.angle,
      binding: object.binding,
      repeatHeader: object.repeatHeader,
      altRowFill: object.altRowFill,
      rowHeightPt: object.rowHeightPt,
      bandFont: object.bandFont,
    })

    const strategy = (table as any).strategy
    if (object.columnWidths && strategy) strategy.columnWidths = [...object.columnWidths]
    if (object.rowHeights && strategy) strategy.rowHeights = [...object.rowHeights]
    if (object.manualRowHeights && strategy) strategy.manualRowHeights = [...object.manualRowHeights]

    for (const data of object.cellData ?? []) {
      const cell: any = table.getCell(data.row, data.col)
      const text: any = table.getCellText(data.row, data.col)
      if (cell) {
        cell.set('fill', data.fill)
        cell.set('stroke', data.stroke)
        cell._hasCustomFill = data.hasCustomFill ?? false
        cell._hasCustomStroke = data.hasCustomStroke ?? false
        cell._colspan = data.colspan ?? 1
        cell._rowspan = data.rowspan ?? 1
        cell._isMerged = data.isMerged ?? false
        cell._mergeParent = data.mergeParent ?? null
        if (cell._isMerged) cell.set('opacity', 0)
      }
      if (text) {
        text.set({
          text: data.text ?? '',
          fill: data.textFill,
          fontSize: data.fontSize,
          fontWeight: data.fontWeight,
          fontStyle: data.fontStyle,
          textAlign: data.textAlign ?? 'left',
        })
        text.verticalAlign = data.verticalAlign ?? 'middle'
        if (cell?._isMerged) text.set('opacity', 0)
      }
    }

    table.triggerLayout()
    return table
  }
}

classRegistry.setClass(DynamicTable, DynamicTable.type)

export function isDynamicTable(o: unknown): o is DynamicTable {
  return o instanceof DynamicTable
}

// ---- Editor surface --------------------------------------------------------

/** True if the first data row's value for this header parses as a number. */
function isNumericColumn(h: string): boolean {
  const v = state.batchData?.[0]?.[h]
  return v != null && v !== '' && !Number.isNaN(Number(v))
}

/** Seed columns from the imported dataset (≤5 so the table stays workable). */
function seedColumns(): { header: string; tmpl: string; numeric: boolean }[] {
  const headers = state.batchHeaders ?? []
  if (headers.length) {
    return headers.slice(0, 5).map((h) => ({ header: h, tmpl: `{{${h}}}`, numeric: isNumericColumn(h) }))
  }
  return [
    { header: 'Description', tmpl: '{{description}}', numeric: false },
    { header: 'Qty', tmpl: '{{qty}}', numeric: true },
    { header: 'Amount', tmpl: '{{ amount | money }}', numeric: true },
  ]
}

/** Create a Dynamic Table: header row + template row + one summary (Total) row. */
export function addDynamicTable(): DynamicTable {
  const { w, h } = labelPx()
  const cols = seedColumns()
  const width = Math.min(Math.max(280, w * 0.85), w - 16)
  const cellWidth = width / cols.length

  const t = new DynamicTable(3, cols.length, {
    cellWidth,
    minCellHeight: 26,
    cellPadding: 6,
    fontSize: 12,
    textAlign: 'left',
    left: w / 2,
    top: Math.min(h / 2, 120 + (h > 600 ? 60 : 0)),
  })

  cols.forEach((col, c) => {
    const head: any = t.getCell(0, c)
    const headTx: any = t.getCellText(0, c)
    head?.set('fill', HEADER_FILL)
    if (head) head._hasCustomFill = true
    headTx?.set({ text: col.header, fill: HEADER_TEXT, fontWeight: 'bold', textAlign: col.numeric ? 'right' : 'left' })

    t.getCellText(1, c)?.set({ text: col.tmpl, textAlign: col.numeric ? 'right' : 'left' })

    const sumTx: any = t.getCellText(2, c)
    sumTx?.set({ fontWeight: 'bold', textAlign: col.numeric ? 'right' : 'left' })
    if (c === 0) sumTx?.set({ text: 'Total' })
    if (c === cols.length - 1 && c > 0) sumTx?.set({ text: '{{ rows | count }}' })
  })
  ;(t as any).triggerLayoutWithAnchor?.()

  const canvas = getCanvas()
  enableTableInteraction(canvas)
  canvas.add(t)
  canvas.setActiveObject(t)
  canvas.requestRenderAll()
  return t
}

/** Write into the currently selected cell (used by the panel's field chips). */
export function setSelectedCellText(t: DynamicTable, text: string): boolean {
  const sel = (t as any)._selectedCells?.[0]
  if (!sel) return false
  t.getCellText(sel.row, sel.col)?.set('text', text)
  ;(t as any).triggerLayoutWithAnchor?.()
  getCanvas().requestRenderAll()
  return true
}
